import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getRequest, patchRequest, deleteRequest } from '../../services/api/apiService';
import { toast } from 'react-toastify';

const TYPE_CONFIG = {
  campaign_created:       { icon: '✅', bg: 'bg-green-50',   border: 'border-green-200',  dot: 'bg-green-500',  label: 'Campaign' },
  campaign_failed:        { icon: '❌', bg: 'bg-red-50',     border: 'border-red-200',    dot: 'bg-red-500',    label: 'Error' },
  subscription_activated: { icon: '🎉', bg: 'bg-yellow-50',  border: 'border-yellow-200', dot: 'bg-yellow-500', label: 'Subscription' },
  subscription_cancelled: { icon: '😔', bg: 'bg-orange-50',  border: 'border-orange-200', dot: 'bg-orange-400', label: 'Subscription' },
  wallet_topup:           { icon: '💰', bg: 'bg-blue-50',    border: 'border-blue-200',   dot: 'bg-blue-500',   label: 'Wallet' },
  payment_failed:         { icon: '⚠️', bg: 'bg-red-50',     border: 'border-red-200',    dot: 'bg-red-500',    label: 'Payment' },
  rule_triggered:         { icon: '⚡', bg: 'bg-purple-50',  border: 'border-purple-200', dot: 'bg-purple-500', label: 'Rule' },
  default:                { icon: '🔔', bg: 'bg-gray-50',    border: 'border-gray-200',   dot: 'bg-gray-400',   label: 'Info' },
};

const FILTERS = ['All', 'Unread', 'Read'];

// Sequelize may return `createdAt` (camelCase) or `created_at` (snake_case)
const getTs = (n) => n.createdAt || n.created_at;

const dayLabel = (n) => {
  const iso = getTs(n);
  if (!iso) return 'Unknown date';
  const d = new Date(iso);
  if (isNaN(d)) return 'Unknown date';
  const now = new Date();
  const diff = Math.floor((now - d) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return 'This week';
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const fmtTime = (n) => {
  const iso = getTs(n);
  if (!iso) return '--:--';
  const d = new Date(iso);
  return isNaN(d) ? '--:--' : d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const fmtDate = (n) => {
  const iso = getTs(n);
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
};

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    try {
      const res = await getRequest('/api/notifications');
      setNotifications(res.data || []);
    } catch {
      toast.error('Unable to load notifications.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markOne = async (id) => {
    try {
      await patchRequest(`/api/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch {
      toast.error('Something went wrong while updating.');
    }
  };

  const markAll = async () => {
    try {
      await patchRequest('/api/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch {
      toast.error('Something went wrong while updating.');
    }
  };

  const deleteOne = async (id, e) => {
    e.stopPropagation();
    setDeletingId(id);
    try {
      await deleteRequest(`/api/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch {
      toast.error('Something went wrong while deleting.');
    } finally {
      setDeletingId(null);
    }
  };

  const deleteAll = async () => {
    if (!window.confirm('Delete all notifications?')) return;
    try {
      await deleteRequest('/api/notifications/delete-all');
      setNotifications([]);
    } catch {
      toast.error('Something went wrong while deleting.');
    }
  };

  const filtered = notifications.filter(n => {
    if (filter === 'Unread') return !n.is_read;
    if (filter === 'Read') return n.is_read;
    return true;
  });

  // Group by relative day label
  const groups = filtered.reduce((acc, n) => {
    const label = dayLabel(n);
    if (!acc[label]) acc[label] = [];
    acc[label].push(n);
    return acc;
  }, {});

  const unread = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
        Loading…
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-[#221C51]">Notifications</h2>
          {unread > 0 && (
            <span className="bg-[#221C51] text-white text-xs font-bold px-2.5 py-1 rounded-full">
              {unread}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unread > 0 && (
            <button onClick={markAll}
              className="text-xs font-medium text-[#221C51] hover:underline transition px-3 py-1.5 rounded-lg hover:bg-gray-100">
              Mark all read
            </button>
          )}
          {notifications.length > 0 && (
            <button onClick={deleteAll}
              className="text-xs font-medium text-red-500 hover:underline transition px-3 py-1.5 rounded-lg hover:bg-red-50">
              Delete all
            </button>
          )}
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1 w-fit">
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filter === f ? 'bg-[#221C51] text-white shadow' : 'text-gray-500 hover:text-[#221C51]'
            }`}>
            {f}
            {f === 'Unread' && unread > 0 && (
              <span className="ml-1.5 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{unread}</span>
            )}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-4xl mb-3">🔔</p>
          <p className="text-sm font-medium">
            {filter === 'Unread' ? 'No unread notifications.' : 'No notifications.'}
          </p>
          <p className="text-xs mt-1 text-gray-400">
            {notifications.length === 0
              ? 'Notifications will appear here when you create campaigns or make payments.'
              : 'Try a different filter.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groups).map(([label, items]) => (
            <div key={label}>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">{label}</p>
              <div className="space-y-2">
                {items.map(n => {
                  const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.default;
                  const isDeleting = deletingId === n.id;
                  return (
                    <div key={n.id}
                      onClick={() => !n.is_read && markOne(n.id)}
                      className={`group relative flex items-start gap-4 p-4 rounded-xl border transition-all ${cfg.bg} ${cfg.border} ${
                        !n.is_read ? 'cursor-pointer hover:shadow-sm hover:opacity-95' : 'opacity-65'
                      } ${isDeleting ? 'opacity-40 pointer-events-none' : ''}`}>

                      {/* Unread dot */}
                      {!n.is_read && (
                        <span className={`absolute top-3 right-10 w-2 h-2 rounded-full ${cfg.dot}`} />
                      )}

                      <div className="text-xl flex-shrink-0 mt-0.5">{cfg.icon}</div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{cfg.label}</span>
                          <p className="font-semibold text-[#221C51] text-sm leading-snug">{n.title}</p>
                        </div>
                        {n.message && (
                          <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{n.message}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                          <p className="text-xs text-gray-400">
                            {fmtDate(n)} at {fmtTime(n)}
                          </p>
                          {n.link && (
                            <Link
                              to={n.link}
                              onClick={e => e.stopPropagation()}
                              className="text-xs font-semibold text-[#221C51] hover:underline flex items-center gap-1"
                            >
                              View campaigns →
                            </Link>
                          )}
                        </div>
                      </div>

                      {/* Delete button */}
                      <button
                        onClick={e => deleteOne(n.id, e)}
                        title="Delete"
                        className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-100 text-gray-300 hover:text-red-500 mt-0.5">
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Notifications;
